import { Fragment, type ReactNode } from 'react';
import { Text, type TextStyle } from 'react-native';
import { twJoin } from 'tailwind-merge';

import { InlineCitationChip } from '@/components/chat/bubbles/references/InlineCitationChip';
import {
  type CitedTextPiece,
  type InlineCitation,
  splitAtCitations,
} from '@/components/chat/bubbles/references/inlineCitations';
import { getAccessibilityProps } from '@/util/accessibilityProps';
import { findMessageLinks, type MessageLinkKind, toMessageLinkHref } from '@/util/messageLinks';
import { getPressProps } from '@/util/pressProps';

/**
 * A run of message text split for rendering: plain text, the words that cite
 * a work, a citation's chip, or a link — a URL, phone number or email address.
 */
export type MessageTextPiece =
  | CitedTextPiece
  | { kind: 'link'; text: string; href: string; linkKind: MessageLinkKind };

/**
 * Splits one run of a message body at its citations and then at its links.
 * `start` is where the run begins in the body. A cited link stays a link: it
 * opens what it points to, and its chip, drawn right after it, opens the
 * reference.
 */
export function splitMessageRun(
  text: string,
  start: number,
  citations: readonly InlineCitation[]
): MessageTextPiece[] {
  return splitAtCitations(text, start, citations).flatMap((piece) =>
    piece.kind === 'citation' ? [piece] : splitLinks(piece)
  );
}

function splitLinks(piece: Exclude<CitedTextPiece, { kind: 'citation' }>): MessageTextPiece[] {
  const parts: MessageTextPiece[] = [];

  let cursor = 0;

  for (const link of findMessageLinks(piece.text)) {
    if (link.start > cursor) {
      parts.push({ ...piece, text: piece.text.slice(cursor, link.start) });
    }

    parts.push({
      kind: 'link',
      text: link.text,
      href: toMessageLinkHref(link),
      linkKind: link.kind,
    });

    cursor = link.end;
  }

  if (cursor < piece.text.length) {
    parts.push({ ...piece, text: piece.text.slice(cursor) });
  }

  return parts;
}

export interface MessageTextPieceOptions {
  position: 'left' | 'right';
  textClassName?: string;
  linkStyle: TextStyle;
  onOpenLink: (href: string) => void;
  onOpenCitation: (number: number) => void;
  /**
   * Draws text and cited words for a run with its own look and press, such as
   * an underlined statement. `onPress` is set for cited words. Without it,
   * text renders plain and cited words open their reference.
   */
  renderRun?: (text: string, key: string, onPress?: () => void) => ReactNode;
}

/**
 * The nodes for a split run, to place inside the message's `Text`. Every
 * message text surface draws its pieces through here, so links, cited words
 * and chips look and behave the same in a statement answer and a plain
 * message.
 *
 * Links and cited words are React Native's own text, which inherits the
 * message's color, size and line height. The Gluestack text brings its own
 * gray and a taller line, which turned cited words dark on an outgoing
 * bubble's green and stretched the line they sat on.
 */
export function renderMessageTextPieces(
  pieces: readonly MessageTextPiece[],
  keyPrefix: string,
  {
    position,
    textClassName,
    linkStyle,
    onOpenLink,
    onOpenCitation,
    renderRun,
  }: MessageTextPieceOptions
): ReactNode[] {
  return pieces.map((piece, pieceIndex) => {
    const key = `${keyPrefix}-${pieceIndex}`;

    if (piece.kind === 'citation') {
      return (
        <Fragment key={key}>
          {NON_BREAKING_SPACE}
          <InlineCitationChip number={piece.number} position={position} onPress={onOpenCitation} />
        </Fragment>
      );
    }

    if (piece.kind === 'link') {
      return (
        <Text
          key={key}
          className={twJoin(textClassName, 'web:cursor-pointer')}
          style={linkStyle}
          {...getPressProps(() => onOpenLink(piece.href))}
          {...getAccessibilityProps({ role: 'link' })}>
          {piece.text}
        </Text>
      );
    }

    if (piece.kind === 'cited') {
      const openCitation = () => onOpenCitation(piece.number);

      if (renderRun) {
        return renderRun(piece.text, key, openCitation);
      }

      return (
        <Text
          key={key}
          className={twJoin(textClassName, 'web:cursor-pointer')}
          {...getPressProps(openCitation)}>
          {piece.text}
        </Text>
      );
    }

    return renderRun ? renderRun(piece.text, key) : piece.text;
  });
}

/** U+00A0 keeps a citation number on the line of the words it cites. */
const NON_BREAKING_SPACE = ' ';
